/**
 * Cliente HTTP do Átrio Contábil — bate no backend Express (porta 3050).
 *
 * Padrão alinhado com api.js do Gesthub: fetch + JSON + headers de auth.
 * Em dev usa AUTH_BYPASS no backend (header X-User); em prod, Bearer JWT.
 */
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const DEV_USER = import.meta.env.VITE_DEV_USER || 'contato@atriocontabilidade.com.br';

function buildHeaders(extra = {}, isFormData = false) {
  const headers = { ...extra };
  // Auth: prioridade JWT (prod), fallback X-User (dev com AUTH_BYPASS=1)
  const token = typeof localStorage !== 'undefined' && localStorage.getItem('atrio_jwt');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    headers['X-User'] = DEV_USER;
  }
  if (!isFormData) headers['Content-Type'] = 'application/json';
  return headers;
}

async function request(path, options = {}) {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const controller = new AbortController();
  const timeoutMs = options.timeout || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: buildHeaders(options.headers, isFormData),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') throw new Error('Tempo limite excedido');
    throw new Error('Falha de conexão com o backend');
  } finally {
    clearTimeout(timeoutId);
  }

  // Token invalido/vencido: limpa e volta ao SSO do Gesthub UMA vez por
  // sessao de aba (guard contra loop se o SSO tambem falhar).
  if ((response.status === 401 || response.status === 403) &&
      typeof window !== 'undefined' && !sessionStorage.getItem('flow_sso_retry')) {
    try {
      sessionStorage.setItem('flow_sso_retry', '1');
      localStorage.removeItem('atrio_jwt');
      const ret = encodeURIComponent('https://contabil.atriocontabilidade.com.br/');
      window.location.href = 'https://gesthub.atriocontabilidade.com.br/api/auth/sso-emit?return_to=' + ret;
    } catch {}
    throw new Error('Sessao expirada, redirecionando para o login...');
  }
  if (response.ok && typeof sessionStorage !== 'undefined') {
    try { sessionStorage.removeItem('flow_sso_retry'); } catch {}
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    return await response.text();  // relatórios HTML
  }

  let payload = null;
  try { payload = await response.json(); } catch { /* sem body */ }

  if (!response.ok) {
    const msg = payload?.message || payload?.error || `HTTP ${response.status}`;
    const err = new Error(msg);
    err.status = response.status;
    err.details = payload?.details || payload?.issues;
    throw err;
  }
  return payload;
}

// ===== Saúde =====
export const saude     = () => request('/saude');
export const stats     = () => request('/saude/stats');

// ===== Clientes (cache Gesthub) =====
export const listarClientes = (filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/clientes${qs ? '?' + qs : ''}`);
};
export const obterClienteApi = (id) => request(`/clientes/${id}`);
export const syncClientes    = () => request('/clientes/sync', { method: 'POST' });
export const syncCliente     = (id) => request(`/clientes/${id}/sync-now`, { method: 'POST' });
export const verificarSituacao = (id) => request(`/clientes/${id}/verificar-situacao`);
export const consultaCnpj    = (cnpj) => request(`/clientes/_consulta-cnpj/${cnpj}`);
export const atualizarConfigCliente = (id, cfg) =>
  request(`/clientes/${id}/config`, { method: 'PATCH', body: JSON.stringify(cfg) });
// Data de abertura pela Receita (BrasilAPI). Sem `forcar`, nao sobrescreve.
export const aberturaReceita = (id, forcar = false) =>
  request(`/clientes/${id}/abertura-receita${forcar ? '?forcar=true' : ''}`, { method: 'POST' });
export const aberturasReceita = () =>
  request('/clientes/_aberturas-receita', { method: 'POST' });

// ===== Plano de contas =====
export const planoReferencial = (filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/plano-contas/referencial${qs ? '?' + qs : ''}`);
};
export const planoCliente = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/plano-contas/cliente/${clienteId}${qs ? '?' + qs : ''}`);
};
export const herdarReferencial = (clienteId) =>
  request(`/plano-contas/cliente/${clienteId}/herdar-referencial`, { method: 'POST' });
export const criarContaCliente = (clienteId, dados) =>
  request(`/plano-contas/cliente/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const atualizarContaCliente = (clienteId, id, dados) =>
  request(`/plano-contas/cliente/${clienteId}/${id}`, { method: 'PATCH', body: JSON.stringify(dados) });

// ===== Períodos =====
export const listarPeriodos = (clienteId) => request(`/periodos/${clienteId}`);
export const abrirPeriodo   = (clienteId, ano, mes) =>
  request(`/periodos/${clienteId}/abrir`, { method: 'POST', body: JSON.stringify({ ano, mes }) });
export const fecharPeriodo  = (clienteId, ano, mes) =>
  request(`/periodos/${clienteId}/fechar`, { method: 'POST', body: JSON.stringify({ ano, mes }) });
export const reabrirPeriodo = (clienteId, ano, mes, motivo) =>
  request(`/periodos/${clienteId}/reabrir`, { method: 'POST', body: JSON.stringify({ ano, mes, motivo }) });

// ===== Lançamentos =====
export const listarLancamentos = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/lancamentos/${clienteId}${qs ? '?' + qs : ''}`);
};
export const obterLancamento = (clienteId, id) => request(`/lancamentos/${clienteId}/${id}`);
// Estorna o original e cria o substituto numa so operacao (usado pelo razao)
export const corrigirLancamento = (clienteId, id, dados) =>
  request(`/lancamentos/${clienteId}/${id}/corrigir`, { method: 'POST', body: JSON.stringify(dados) });
export const criarLancamento = (clienteId, dados) =>
  request(`/lancamentos/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
// Importação por CSV: modo 'simular' valida sem gravar; 'gravar' grava tudo ou nada
export const importarLancamentos = (clienteId, csv, modo = 'simular') =>
  request(`/lancamentos/${clienteId}/importar`, {
    method: 'POST', body: JSON.stringify({ csv, modo }), timeout: 120000,
  });
export const atualizarLancamento = (clienteId, id, dados) =>
  request(`/lancamentos/${clienteId}/${id}`, { method: 'PUT', body: JSON.stringify(dados) });
export const excluirLancamento = (clienteId, id) =>
  request(`/lancamentos/${clienteId}/${id}`, { method: 'DELETE' });
export const aprovarLancamento = (clienteId, id) =>
  request(`/lancamentos/${clienteId}/${id}/aprovar`, { method: 'POST' });
export const aprovarLote = (clienteId, ids) =>
  request(`/lancamentos/${clienteId}/aprovar-lote`, { method: 'POST', body: JSON.stringify({ ids }) });
export const estornarLancamento = (clienteId, id, motivo) =>
  request(`/lancamentos/${clienteId}/${id}/estornar`, { method: 'POST', body: JSON.stringify({ motivo }) });
export const balancete = (clienteId, ano, mes) =>
  request(`/lancamentos/${clienteId}/relatorios/balancete?ano=${ano}&mes=${mes}`);

// ===== Reinf =====
export const listarR4020 = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/reinf/r4020/${clienteId}${qs ? '?' + qs : ''}`);
};
export const obterR4020 = (clienteId, id) => request(`/reinf/r4020/${clienteId}/${id}`);
export const criarR4020 = (clienteId, dados) =>
  request(`/reinf/r4020/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const enviarR4020Req = (id) => request(`/reinf/r4020/${id}/enviar`, { method: 'POST' });
// R-4010 (retenções a PF — lucros/dividendos tributados)
export const listarR4010 = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/reinf/r4010/${clienteId}${qs ? '?' + qs : ''}`);
};
export const obterR4010 = (clienteId, id) => request(`/reinf/r4010/${clienteId}/${id}`);
export const enviarR4010Req = (id) => request(`/reinf/r4010/${id}/enviar`, { method: 'POST' });
export const listarNaturezas = (filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/reinf/naturezas${qs ? '?' + qs : ''}`);
};
export const calcularRet = (dados) =>
  request('/reinf/calcular-retencao', { method: 'POST', body: JSON.stringify(dados) });
export const listarEnviosReinf = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/reinf/envios/${clienteId}${qs ? '?' + qs : ''}`);
};

// Dividendos (Lei 15.270/2025) — mensal e trimestral
export const listarDividendos = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/reinf/dividendos-por-beneficiario/${clienteId}${qs ? '?' + qs : ''}`);
};
export const detalheTrimestre = (clienteId, trimestre) =>
  request(`/reinf/dividendos-trimestre/${clienteId}/${trimestre}/detalhe`);
export const simularDividendo = (clienteId, dados) =>
  request(`/reinf/simular-dividendo/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const listarDeliberacoes = (clienteId) =>
  request(`/reinf/deliberacoes/${clienteId}`);
export const criarDeliberacao = (clienteId, dados) =>
  request(`/reinf/deliberacoes/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const listarOutrosDadosIsento = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/reinf/outros-dados-isento/${clienteId}${qs ? '?' + qs : ''}`);
};

// ===== Workers + pendências =====
export const rodarCrossmatch = (cliente_id, per_apur, cnpj_cliente) =>
  request('/workers/finance-cross/rodar', { method: 'POST',
    body: JSON.stringify({ cliente_id, per_apur, cnpj_cliente }) });
export const rodarContabilizacao = (cliente_id, per_apur) =>
  request('/workers/finance-to-lcto/rodar', { method: 'POST',
    body: JSON.stringify({ cliente_id, per_apur }) });
export const listarPendenciasReinf = (clienteId, filtros = {}) => {
  const qs = new URLSearchParams(filtros).toString();
  return request(`/workers/pendencias/${clienteId}${qs ? '?' + qs : ''}`);
};
export const descartarPendencia = (id, motivo) =>
  request(`/workers/pendencias/${id}/descartar`, { method: 'POST', body: JSON.stringify({ motivo }) });
export const listarTxSemLcto = (clienteId, resolvido = false) =>
  request(`/imports/tx-sem-lancamento/${clienteId}?resolvido=${resolvido}`);

// ===== Certificados =====
export const listarCerts   = (clienteId) => request(`/certificados/${clienteId}`);
export const certsAlerta   = () => request('/certificados/_alerta/all');
export const certAudit     = (clienteId, id) => request(`/certificados/${clienteId}/${id}/audit`);
export const uploadCert    = (clienteId, file, senha) => {
  const form = new FormData();
  form.append('pfx', file);
  form.append('senha', senha);
  return request(`/certificados/${clienteId}/upload`, { method: 'POST', body: form });
};
export const revogarCert = (clienteId, id, motivo) =>
  request(`/certificados/${clienteId}/${id}/revogar`, { method: 'POST', body: JSON.stringify({ motivo }) });

// ===== Mapeamento Finance =====
export const listarMapeamento = (clienteId) => request(`/mapeamento/${clienteId}`);
export const salvarMapeamento = (clienteId, dados) =>
  request(`/mapeamento/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const aplicarTemplateMapa = (clienteId) =>
  request(`/mapeamento/${clienteId}/aplicar-template`, { method: 'POST' });

// ===== Mapeamento Notas (escrituração automática de NFS-e) =====
export const listarMapeamentoNotas = (clienteId) => request(`/mapeamento/notas/${clienteId}`);
export const salvarMapeamentoNotas = (clienteId, dados) =>
  request(`/mapeamento/notas/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const aplicarTemplateNotas = (clienteId) =>
  request(`/mapeamento/notas/${clienteId}/aplicar-template`, { method: 'POST' });
export const rodarNotasToLcto = (cliente_id, per_apur) =>
  request('/workers/notas-to-lcto/rodar', { method: 'POST',
    body: JSON.stringify({ cliente_id, per_apur }) });
export const listarNotasSemLcto = (clienteId, resolvido = false) =>
  request(`/workers/notas-sem-lcto/${clienteId}?resolvido=${resolvido}`);
export const resolverNotaSemLcto = (id) =>
  request(`/workers/notas-sem-lcto/${id}/marcar-resolvida`, { method: 'POST' });

// ===== Relatórios =====
export const dre = (clienteId, params) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/relatorios/dre/${clienteId}?${qs}`);
};
export const balanco = (clienteId, params) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/relatorios/balanco/${clienteId}?${qs}`);
};
export const faturamento12 = (clienteId, params) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/relatorios/faturamento12/${clienteId}?${qs}`);
};
export const urlHtmlRelatorio = (tipo, clienteId, params) => {
  const qs = new URLSearchParams({ ...params, _t: Date.now() }).toString();
  return `${API_BASE}/relatorios/${tipo}/${clienteId}/html?${qs}`;
};
// PDF gerado no servidor: mesmo caminho e mesma consulta do HTML.
export const urlPdfRelatorio = (tipo, clienteId, params) => {
  const qs = new URLSearchParams(params).toString();
  return `${API_BASE}/relatorios/pdf/${tipo}/${clienteId}?${qs}`;
};
export const razaoContas = (clienteId, params) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/relatorios/razao-contas/${clienteId}?${qs}`);
};

// ===== Snapshots (fechar relatório + liberação ao cliente) =====
export const listarSnapshots = (clienteId) => request(`/relatorios/snapshot/${clienteId}`);
export const criarSnapshot = (clienteId, dados) =>
  request(`/relatorios/snapshot/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const liberarSnapshot = (clienteId, id) =>
  request(`/relatorios/snapshot/${clienteId}/${id}/liberar`, { method: 'POST' });
export const revogarSnapshot = (clienteId, id) =>
  request(`/relatorios/snapshot/${clienteId}/${id}/revogar-liberacao`, { method: 'POST' });

// ===== Imports =====
export const uploadDominioFolha = (clienteId, file, aplicar = false) => {
  const form = new FormData();
  form.append('arquivo', file);
  const path = aplicar
    ? `/imports/dominio-folha/${clienteId}/upload-e-aplicar`
    : `/imports/dominio-folha/${clienteId}/upload`;
  return request(path, { method: 'POST', body: form });
};
export const listarImportsDominio = (clienteId) => request(`/imports/dominio-folha/${clienteId}`);

// IRPFM Anual (Camada 2 — Lei 15.270/2025)
export const simularIrpfm = (dados) =>
  request('/reinf/irpfm/simular', { method: 'POST', body: JSON.stringify(dados) });
export const previewIrpfm = (clienteId, doc, ano) =>
  request(`/reinf/irpfm/${clienteId}/${doc}/${ano}/preview`);
export const apurarIrpfmReq = (clienteId, doc, ano, dados = {}) =>
  request(`/reinf/irpfm/${clienteId}/${doc}/${ano}/apurar`, { method: 'POST', body: JSON.stringify(dados) });
export const listarApuracoesIrpfm = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/reinf/irpfm/${clienteId}${qs ? '?' + qs : ''}`);
};

// Rendimentos paralelos (Camada 3)
export const listarRendaParalela = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/reinf/renda-paralela/${clienteId}${qs ? '?' + qs : ''}`);
};
export const criarRendaParalela = (clienteId, dados) =>
  request(`/reinf/renda-paralela/${clienteId}`, { method: 'POST', body: JSON.stringify(dados) });
export const apagarRendaParalela = (id) =>
  request(`/reinf/renda-paralela/${id}`, { method: 'DELETE' });

// ===== Apuração DAS Simples Nacional =====
export const listarApuracoesDAS = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/apuracao-das/${clienteId}${qs ? '?' + qs : ''}`);
};
export const obterApuracaoDAS = (clienteId, competencia) =>
  request(`/apuracao-das/${clienteId}/${competencia}`);
export const apurarDAS = (clienteId, dados) =>
  request(`/apuracao-das/${clienteId}/apurar`, { method: 'POST', body: JSON.stringify(dados) });
export const simularDAS = (dados) =>
  request('/apuracao-das/simular', { method: 'POST', body: JSON.stringify(dados) });
export const aprovarDAS = (id) =>
  request(`/apuracao-das/${id}/aprovar`, { method: 'POST' });
export const refAnexosDAS = () =>
  request('/apuracao-das/_ref/anexos');

// ===== Apuração PIS/COFINS =====
export const listarApuracoesPisCofins = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/apuracao-pis-cofins/${clienteId}${qs ? '?' + qs : ''}`);
};
export const apurarPisCofins = (clienteId, dados) =>
  request(`/apuracao-pis-cofins/${clienteId}/apurar`, { method: 'POST', body: JSON.stringify(dados) });
export const simularPisCofins = (dados) =>
  request('/apuracao-pis-cofins/simular', { method: 'POST', body: JSON.stringify(dados) });
export const aprovarPisCofins = (id) =>
  request(`/apuracao-pis-cofins/${id}/aprovar`, { method: 'POST' });

// ===== Apuração IRPJ/CSLL Lucro Presumido (trimestral) =====
export const listarApuracoesIrpjCsll = (clienteId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/apuracao-irpj-csll/${clienteId}${qs ? '?' + qs : ''}`);
};
export const apurarIrpjCsll = (clienteId, dados) =>
  request(`/apuracao-irpj-csll/${clienteId}/apurar`, { method: 'POST', body: JSON.stringify(dados) });
export const aprovarIrpjCsll = (id) =>
  request(`/apuracao-irpj-csll/${id}/aprovar`, { method: 'POST' });
export const refPresuncoesIrpjCsll = () =>
  request('/apuracao-irpj-csll/_ref/presuncoes');

// ===== Healthcheck cross-system =====
export const saudeCrossSystem = (refresh = false) =>
  request(`/saude/cross-system${refresh ? '?refresh=1' : ''}`);

// ===== Integrações externas =====
export const listarIntegracoes = () => request('/integracoes');
export const obterIntegracao = (codigo) => request(`/integracoes/${codigo}`);
export const atualizarIntegracao = (codigo, dados) =>
  request(`/integracoes/${codigo}`, { method: 'PUT', body: JSON.stringify(dados) });
export const testarIntegracaoReq = (codigo) =>
  request(`/integracoes/${codigo}/testar`, { method: 'POST' });
export const limparCredenciaisIntegracao = (codigo) =>
  request(`/integracoes/${codigo}/credenciais`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Central de alertas fiscais
// ---------------------------------------------------------------------------
export const listarAlertas = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '')).toString();
  return request(`/alertas${q ? `?${q}` : ''}`);
};
export const listarAlertasCliente = (clienteId, status = 'aberto') =>
  request(`/alertas/${clienteId}?status=${status}`);
export const avaliarAlertas = (clienteId = null) =>
  request('/alertas/avaliar', {
    method: 'POST',
    body: JSON.stringify(clienteId ? { cliente_id: clienteId } : {}),
    timeout: 300000,
  });
export const tratarAlerta = (id, acao, observacao = null) =>
  request(`/alertas/${id}/${acao}`, {
    method: 'POST', body: JSON.stringify({ observacao }),
  });
export const catalogoAlertas = () => request('/alertas/catalogo/regras');
export const configurarAlerta = (regra, corpo) =>
  request(`/alertas/catalogo/${regra}`, { method: 'PUT', body: JSON.stringify(corpo) });

// ---------------------------------------------------------------------------
// Trava manual da escrituração
// ---------------------------------------------------------------------------
export const consultarBloqueio = (clienteId) =>
  request(`/periodos/${clienteId}/bloqueio`);
export const definirBloqueio = (clienteId, bloqueada_ate, motivo) =>
  request(`/periodos/${clienteId}/bloqueio`, {
    method: 'PUT', body: JSON.stringify({ bloqueada_ate, motivo }),
  });
export const fecharPeriodosAte = (clienteId, competencia) =>
  request(`/periodos/${clienteId}/fechar-ate`, {
    method: 'POST', body: JSON.stringify({ competencia }),
  });

// ---- Preferencias do usuario logado (padrao de impressao, de pacote...) ----
export const obterPreferencias  = () => request('/preferencias');
export const salvarPreferencias = (dados) =>
  request('/preferencias', { method: 'PUT', body: JSON.stringify(dados) });

/**
 * Abre um HTML de rota autenticada numa aba nova.
 *
 * A aba e aberta AGORA, no gesto do usuario — dentro do .then() o navegador
 * bloqueia como pop-up. O JWT vai no header, o que `window.open(url)` nao faz.
 */
export async function abrirDocumentoAutenticado(path, rotulo = 'documento') {
  const aba = window.open('', '_blank');
  if (aba) {
    aba.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Gerando ${rotulo}...</title></head>
      <body style="font-family:system-ui,sans-serif;display:grid;place-items:center;
                   height:100vh;margin:0;background:#f4f1ee;color:#5A5048">
      <p>Gerando ${rotulo}...</p></body></html>`);
    aba.document.close();
  }
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: buildHeaders() });
    const texto = await r.text();
    if (!r.ok) throw new Error(texto.slice(0, 200) || `HTTP ${r.status}`);
    if (aba) { aba.document.open(); aba.document.write(texto); aba.document.close(); }
    return texto;
  } catch (err) {
    if (aba) {
      aba.document.open();
      aba.document.write(`<p style="font-family:system-ui,sans-serif;padding:24px;color:#B04A4A">
        Nao foi possivel gerar: ${String(err.message || err)}</p>`);
      aba.document.close();
    }
    throw err;
  }
}
